import { z } from 'zod';
import { Validation } from '@mews/framework';
import {
    MultipleRateGroupsAddParameters,
    MultipleRateGroupsUpdateParameters,
    RateGroupOptions,
    RateGroupWithPaymentPolicies,
    SettlementExtent,
    SettlementRuleAction,
    SettlementRuleTrigger,
    SettlementRuleType,
    SettlementValueType,
} from '@mews-commander/data-access';
import { createUpdateValueObject } from '@mews-commander/utils';

/**
 * Now, if we could get this out of the zod schema, it would be great.
 */
export const RateGroupFormSchemaKeys = {
    serviceId: 'serviceId',
    rateGroupId: 'rateGroupId',
    names: 'names',
    shortNames: 'shortNames',
    descriptions: 'descriptions',
    ordering: 'ordering',
    externalIdentifier: 'externalIdentifier',

    settlementMaximumTimeUnits: 'settlementMaximumTimeUnits',
    settlementExtent: 'settlementExtent',
    options: 'options',
    settlementCurrencyCode: 'settlementCurrencyCode',

    settlementRuleAction: 'settlementRuleAction',
    settlementRuleType: 'settlementRuleType',
    settlementRuleTrigger: 'settlementRuleTrigger',
    settlementRuleOffset: 'settlementRuleOffset',
    settlementRuleValue: 'settlementRuleValue',

    created: 'created',
    updated: 'updated',
} as const;

export const SettlementRuleFormSchemaKeys = {
    settlementRuleId: 'settlementRuleId',
    action: 'action',
    type: 'type',
    trigger: 'trigger',
    offset: 'offset',
    value: 'value',
} as const;

export const SettlementRuleSectionPrefix = 'settlementRule';

const ActorTimestampSchema = z.object({
    utc: z.string(),
    profileId: z.string(),
});

const SettlementRuleSchema = z.object({
    [SettlementRuleFormSchemaKeys.action]: z.nativeEnum(SettlementRuleAction),
    [SettlementRuleFormSchemaKeys.type]: z.nativeEnum(SettlementRuleType),
    [SettlementRuleFormSchemaKeys.trigger]: z.nativeEnum(SettlementRuleTrigger),
    [SettlementRuleFormSchemaKeys.offset]: z.string().default('PT0S'),
    [SettlementRuleFormSchemaKeys.value]: z.discriminatedUnion('discriminator', [
        z.object({
            discriminator: z.literal(SettlementValueType.Flat),
            value: z.object({
                value: z.number().positive(),
            }),
        }),
        z.object({
            discriminator: z.literal(SettlementValueType.Percentage),
            value: z.object({
                value: z.number().min(0).max(1),
            }),
        }),
    ]),
});

export const createRateGroupCreateFormModel = ({ languageCode }: { languageCode: string }) =>
    z.object({
        [RateGroupFormSchemaKeys.serviceId]: z.string(),
        /**
         * The passthrough allows to capture non-default languages too, while the object gives a nested
         * error message on missing values.
         */
        [RateGroupFormSchemaKeys.names]: z.object({ [languageCode]: Validation.String.Required }).passthrough(),
        [RateGroupFormSchemaKeys.shortNames]: z.record(z.string()),
        [RateGroupFormSchemaKeys.descriptions]: z.record(z.string()),
        [RateGroupFormSchemaKeys.ordering]: z.number().int().nullish(),
        [RateGroupFormSchemaKeys.externalIdentifier]: z.string().nullish(),

        [RateGroupFormSchemaKeys.settlementMaximumTimeUnits]: z.number().int().positive().nullish(),
        [RateGroupFormSchemaKeys.settlementExtent]: z.array(z.nativeEnum(SettlementExtent)).min(1),
        [RateGroupFormSchemaKeys.options]: z.array(z.nativeEnum(RateGroupOptions)),
        [RateGroupFormSchemaKeys.settlementCurrencyCode]: z.string(),
        [SettlementRuleSectionPrefix]: SettlementRuleSchema,
    });

export type RateGroupCreateFormModel = z.infer<ReturnType<typeof createRateGroupCreateFormModel>>;

export const createRateGroupCreateFormValidator = (model: ReturnType<typeof createRateGroupCreateFormModel>) =>
    model.transform(
        ({ serviceId, settlementRule, ...rest }) =>
            ({
                serviceId,
                rateGroups: [
                    {
                        ...rest,
                        serviceId,
                        settlementRules: [settlementRule],
                    },
                ],
            }) satisfies MultipleRateGroupsAddParameters
    );

export const createRateGroupEditFormModel = ({ languageCode }: { languageCode: string }) => {
    const createModel = createRateGroupCreateFormModel({ languageCode });
    return createModel.extend({
        [RateGroupFormSchemaKeys.rateGroupId]: z.string(),
        [RateGroupFormSchemaKeys.created]: ActorTimestampSchema,
        [RateGroupFormSchemaKeys.updated]: ActorTimestampSchema,
        [SettlementRuleSectionPrefix]: SettlementRuleSchema.extend({
            [SettlementRuleFormSchemaKeys.settlementRuleId]: z.string(),
        }),
    });
};

export type RateGroupEditFormModel = z.infer<ReturnType<typeof createRateGroupEditFormModel>>;

export const transformRateGroupDto = ({
    id: rateGroupId,
    serviceId,
    names,
    shortNames,
    descriptions,
    ordering,
    externalIdentifier,
    settlementMaximumTimeUnits,
    settlementExtent,
    options,
    settlementCurrencyCode,
    settlementRules,
    createdUtc,
    creatorProfileId,
    updatedUtc,
    updaterProfileId,
}: RateGroupWithPaymentPolicies) => {
    const firstSettlementRule = settlementRules.find((s) => s.isFirst);
    if (!firstSettlementRule) {
        throw new Error('There is no default SettlementRule');
    }

    const { id: settlementRuleId, action, type, trigger, offset, value } = firstSettlementRule;

    return {
        rateGroupId,
        serviceId,
        names,
        shortNames,
        descriptions,
        ordering,
        settlementExtent,
        options,
        settlementCurrencyCode,
        externalIdentifier: externalIdentifier ?? undefined,
        settlementMaximumTimeUnits: settlementMaximumTimeUnits ?? undefined,
        created: {
            utc: createdUtc,
            profileId: creatorProfileId,
        },
        updated: {
            utc: updatedUtc,
            profileId: updaterProfileId,
        },
        settlementRule: {
            settlementRuleId,
            action,
            type,
            trigger,
            offset,
            value,
        },
    } satisfies RateGroupEditFormModel;
};

export const createRateGroupEditFormValidator = (
    model: ReturnType<typeof createRateGroupEditFormModel>,
    originalObject: RateGroupEditFormModel
) =>
    model.transform(
        ({
            serviceId,
            rateGroupId,
            settlementCurrencyCode,
            created,
            updated,
            settlementRule: { settlementRuleId, ...settlementRule },
            ...rateGroup
        }) =>
            ({
                serviceId,
                rateGroupUpdates: [
                    {
                        rateGroupId,
                        ...createUpdateValueObject(originalObject, rateGroup),
                        settlementRuleUpdates: [
                            {
                                settlementRuleId,
                                ...createUpdateValueObject(originalObject.settlementRule, settlementRule),
                            },
                        ],
                    },
                ],
            }) satisfies MultipleRateGroupsUpdateParameters
    );
